import { ChevronRight, ChevronsRight, ListTree, RotateCw, ShieldCheck } from "lucide-react"
import { useState } from "react"
import { useSearch } from "@tanstack/react-router"
import { HairlineTable } from "@/components/archetypes/HairlineTable"
import { Mono } from "@/components/copy-button"
import { JsonView } from "@/components/json-view"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { ActionBar } from "@/components/ui/action-bar"
import { Button } from "@/components/ui/button"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { useT } from "@/lib/i18n"
import { LEDGER_PAGE, matchesLedgerReceipt, useLedger } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { asString, isRecord, shortHash } from "@/lib/utils"

/** What an event is, in one phrase: its declared type or kind. */
export function eventLabel(event: unknown, t: ReturnType<typeof useT>): string {
  if (!isRecord(event)) return t("ledger.eventFallback")
  return (
    asString(event.type) ??
    asString(event.kind) ??
    asString(event.event) ??
    asString(event.op) ??
    t("ledger.eventFallback")
  )
}

export function eventSubject(event: unknown): string | undefined {
  if (!isRecord(event)) return undefined
  const type = asString(event.objectType) ?? asString(event.entityKind)
  const key = asString(event.key) ?? asString(event.objectKey) ?? asString(event.objectId)
  if (type && key) return `${type} / ${key}`
  return asString(event.subject) ?? asString(event.actionId) ?? asString(event.ruleId)
}

export function LedgerPage() {
  const search = useSearch({ from: "/$orgId/$environmentId/ledger" })
  return <LedgerView key={`${search.commit}/${search.after}/${search.seq}/${search.hash}`} search={search} />
}

function LedgerView({ search }: { search: { after?: string; seq?: string; commit?: string; hash?: string } }) {
  const t = useT()
  const scope = useScope()
  const requestedCommit = search.commit === undefined ? undefined : Number(search.commit)
  // `after` is a ledger sequence position; the trail lets the operator step back.
  const [trail, setTrail] = useState<number[]>([])
  const [afterEdit, setAfter] = useState<number | null>(null)
  const [openEdit, setOpen] = useState<number | null | undefined>(undefined)
  const after = afterEdit ?? (search.after === undefined ? 0 : Number(search.after))
  const page = useLedger(scope, after, requestedCommit)
  const changed = requestedCommit !== undefined && page.isSuccess && !matchesLedgerReceipt(page.data.receipt, requestedCommit, search.hash)
  const open = openEdit === undefined ? (search.seq === undefined ? requestedCommit === undefined ? null : page.data?.entries[0]?.seq : Number(search.seq)) : openEdit
  const entry = page.data?.entries.find((item) => item.seq === open)
  const last = page.data?.entries.at(-1)?.seq
  const receipt = page.data?.receipt
  const atHead = requestedCommit !== undefined
    ? !receipt || receipt.eventCount === 0 || (last ?? after) >= receipt.firstEventSeq + receipt.eventCount - 1
    : page.data ? (last ?? after) >= page.data.headSeq : true

  return (
    <>
      <PageHeader
        crumbs={[t("nav.ledger")]}
        actions={
          <>
            {page.data ? (
              <span
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
                title={t("ledger.verifiedHeadHash", { hash: page.data.headHash })}
              >
                <ShieldCheck className="size-3.5 text-success" aria-hidden />
                {t("ledger.head")} <span className="font-mono tabular">#{page.data.headSeq}</span>
                <code className="font-mono text-2xs text-subtle-foreground">{shortHash(page.data.headHash, 10)}</code>
              </span>
            ) : null}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => void page.refetch()}
              disabled={page.isFetching}
              aria-label={t("common.refresh")}
            >
              <RotateCw className={page.isFetching ? "animate-spin" : undefined} />
            </Button>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width="board" className="px-4 py-6 md:px-6">
          <div>
            {changed ? (
              <EmptyState icon={<ListTree />} title={t("ledger.receiptReset")}>
                <p>{t("ledger.receiptResetHelp")}</p>
              </EmptyState>
            ) : page.isPending ? (
              <TableSkeleton columns={5} />
            ) : page.isError ? (
              <ErrorState error={page.error} what={t("ledger.theLedger")} onRetry={() => void page.refetch()} />
            ) : requestedCommit !== undefined && receipt?.eventCount === 0 ? (
              <EmptyState icon={<ShieldCheck />} title={t("ledger.actionRecorded")}>
                <p>{t("ledger.actionRecordedHelp")}</p>
              </EmptyState>
            ) : page.data.entries.length === 0 ? (
              <EmptyState
                icon={<ListTree />}
                title={after === 0 ? t("ledger.empty") : t("ledger.nothingAfterPosition")}
              >
                <p>{t("ledger.emptyDescription")}</p>
              </EmptyState>
            ) : (
              <Section>
                <HairlineTable
                  rows={page.data.entries}
                  getRowKey={(row) => String(row.seq)}
                  onRowClick={(row) => setOpen(open === row.seq ? null : row.seq)}
                  columns={[
                    {
                      key: "seq",
                      header: t("ledger.seq"),
                      align: "end",
                      cell: (row) => (
                        <button
                          type="button"
                          aria-expanded={open === row.seq}
                          onClick={(event) => {
                            event.stopPropagation()
                            setOpen(open === row.seq ? null : row.seq)
                          }}
                          className="inline-flex items-center gap-1 font-mono text-xs"
                        >
                          <ChevronRight
                            className={`rtl-mirror size-3 text-subtle-foreground ${open === row.seq ? "rotate-90" : ""}`}
                            aria-hidden
                          />
                          {row.seq}
                        </button>
                      ),
                    },
                    {
                      key: "commit",
                      header: t("ledger.commit"),
                      align: "end",
                      cell: (row) => <code className="font-mono text-xs text-muted-foreground">{row.commitSeq}</code>,
                    },
                    {
                      key: "event",
                      header: t("ledger.event"),
                      cell: (row) => <code className="font-mono text-xs">{eventLabel(row.event, t)}</code>,
                    },
                    {
                      key: "subject",
                      header: t("ledger.subject"),
                      cellClassName: "max-w-80 truncate text-muted-foreground",
                      cell: (row) => eventSubject(row.event) ?? "",
                    },
                    {
                      key: "world",
                      header: t("ledger.world"),
                      cell: (row) => <code className="font-mono text-2xs text-subtle-foreground">{row.world}</code>,
                    },
                    {
                      key: "hash",
                      header: t("ledger.hash"),
                      cell: (row) => (
                        <code className="font-mono text-2xs text-subtle-foreground" title={row.hash}>
                          {shortHash(row.hash, 12)}
                        </code>
                      ),
                    },
                  ]}
                />
                {entry ? (
                  <Section
                    bordered
                    title={
                      <code className="font-mono text-sm">
                        {eventLabel(entry.event, t)} · #{entry.seq}
                      </code>
                    }
                    contentClassName="p-4"
                  >
                    <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        {t("ledger.hashLabel")} <Mono value={entry.hash} display={shortHash(entry.hash, 24)} />
                      </span>
                      <span className="flex items-center gap-1">
                        {t("ledger.prevLabel")} <Mono value={entry.prevHash} display={shortHash(entry.prevHash, 24)} />
                      </span>
                    </div>
                    <JsonView value={entry.event} className="max-h-80" />
                  </Section>
                ) : null}
              </Section>
            )}
          </div>
          <ActionBar
            className="max-md:bottom-0"
            status={
              <span className="text-xs text-muted-foreground tabular">
                {page.data && page.data.entries.length > 0
                  ? t("ledger.range", {
                      first: page.data.entries[0]?.seq ?? "",
                      last: last ?? "",
                      head: page.data.headSeq,
                    })
                  : t("ledger.afterPosition", { after })}
              </span>
            }
          >
            <Button
              size="sm"
              disabled={trail.length === 0 || page.isFetching}
              onClick={() => {
                setAfter(trail.at(-1) ?? 0)
                setTrail((t) => t.slice(0, -1))
              }}
            >
              {t("ledger.back")}
            </Button>
            <Button
              size="sm"
              disabled={changed || atHead || page.isFetching}
              onClick={() => {
                if (last === undefined) return
                setTrail((t) => [...t, after])
                setAfter(last)
              }}
            >
              {t("ledger.nextPage", { count: LEDGER_PAGE })} <ChevronRight className="rtl-mirror" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={requestedCommit !== undefined || atHead || page.isFetching || !page.data}
              onClick={() => {
                if (!page.data) return
                setTrail((t) => [...t, after])
                setAfter(Math.max(0, page.data.headSeq - LEDGER_PAGE))
              }}
            >
              {t("ledger.latest")} <ChevronsRight />
            </Button>
          </ActionBar>
        </Screen>
      </div>
    </>
  )
}
